import {
    ChoiceGroup,
    DefaultButton,
    FontWeights,
    IPanelProps,
    IRenderFunction,
    Panel,
    PanelType,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack, Text, TextField
} from "@fluentui/react";
import { RootState } from "app/store";
import { createAccountsReceivable, emitCustomEvent, fadeIn, panelButtonsStyles, sectionStackTokens } from "global/Application";
import { addUpdateAccountsReceivable } from "global/Reducer/dataSlice";
import { IPanelEvent, IPickItem, IValueChoiceGroupOption } from "model/Extension";
import {
    IAccountsReceivable as FAM_IAccountsReceivable
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { ConnectedProps, connect, useDispatch } from "react-redux";
import AccountsReceivableCard from "./AccountsReceivableCard";
import {
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from "model/CRM";
import { useMsal } from "@azure/msal-react";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { JsonTransformer } from "global/Transform/JsonTransformer";
import { updateArrayReplace } from "global/Utility/TSFunctions";
import { ECardIcon } from "components/controls/SimpleCard";
import SnapshotLookup from "./Controls/SnapshotLookup";

interface IConfirm {
    ARSelected?: FAM_IAccountsReceivable | null;
}

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IConfirm>) => void;

    // start condition
    defaultPayload?: IConfirm;

    accountReceivable?: FAM_IAccountsReceivable

    variant: "pick" | "edit";
}

enum EView {
    entry,
    add,
    select,
    copy,
    import,
};


const AccountsReceivableWizard: React.FunctionComponent<IProps> = (props) => {
    // current view state/stages
    const dispatch = useDispatch();

    const [view, setView] = useState<EView | null>(EView.entry);
    const [optionView, setOptionSource] = useState<IValueChoiceGroupOption<EView> | null>(null);

    const [loading, setLoading] = useState<Boolean>(false);

    const [entityCollection, setEntityCollection] = useState<IPickItem<FAM_IAccountsReceivable>[] | null>(null);
    const [importSnapshot, setImportSnapshot] = useState<IXrmTypes_bcbs_groupconfigurationsnapshot | null>();


    const disabledAdd = props.variant !== "edit";
    const disabledLink = props.variant !== "pick";

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const optionsView: IValueChoiceGroupOption<EView>[] = [
        { key: 'add', text: 'Add - New Accounts Receivable', data: EView.add, disabled: disabledAdd },
        { key: 'pick', text: 'Pick - From Current Configuration', data: EView.select, disabled: disabledLink },
        { key: 'copy', text: 'Copy/Clone - From Current Configuration', data: EView.copy, disabled: disabledAdd },
        { key: 'import', text: 'Copy/Clone - From External Configuration/BlueRelationship', data: EView.import, disabled: disabledAdd },

    ];

    useEffect(() => {
        if (props.isOpen) {
            if (props.variant === 'pick') {
                setView(EView.select);
                setOptionSource(optionsView.find(t => t.data === EView.select)!);
            } else {
                setView(EView.entry);
            }
            setEntityCollection(null);
            setImportSnapshot(null);
        }
    }, [props.isOpen])


    useEffect(() => {
        if (view === EView.entry) {
            setEntityCollection(null);
            setImportSnapshot(null);
            setEntityCollection(null);
        }
        else if (view === EView.copy || view === EView.import) {
            (async () => {
                try {
                    setLoading(true);

                    setEntityCollection(null);

                    const { jClone, sClone } = await (async () => {

                        if (view === EView.import && importSnapshot) {
                            const result = await dataSource.downloadGroupConfigurationSnapshot(importSnapshot);

                            if (!result)
                                throw new Error('Snapshot not found!');

                            return { jClone: result.json, sClone: structuredClone(importSnapshot) };
                        }
                        else if (view === EView.copy)
                            return { jClone: structuredClone(props.state.data.json), sClone: structuredClone(props.state.data.snapshot) };

                        return { jClone: null, sClone: null };
                    })();

                    if (!jClone || !sClone)
                        return;

                    const transformer = JsonTransformer.getTransformerForCopyInternal(
                        props.state.data.config,
                        dataSource,
                        props.state.data.snapshot,
                        { applyToAccountsReceivables: true }
                    );

                    await transformer.transformAsync(jClone, sClone);

                    const data = jClone;

                    const collection: IPickItem<FAM_IAccountsReceivable>[] = [];

                    data.claimsARs.forEach(eachEntity => {

                        // transformer above is already taking care of this
                        // lets skip deactivated, they shouldn't be able to pick
                        if (eachEntity.deactivated)
                            return;

                        collection.push({
                            item: eachEntity,
                            selected: false
                        });
                    });

                    setEntityCollection(collection);
                } catch (error) {
                    // lets create a new one is payload is not providing one
                    emitCustomEvent({
                        eventType: 'globalException',
                        details: error
                    });

                } finally {
                    setLoading(false);
                }

            })();
        }

    }, [view, importSnapshot]);


    const onRenderEntry: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        return (
            <ChoiceGroup
                options={optionsView}
                label="Choose Source"
                required={true}
                selectedKey={optionView?.key}
                onChange={(event, option?: IValueChoiceGroupOption<EView>) => {
                    setOptionSource(option ?? null);
                }}
            />
        )
    }

    const onRenderEntryFooter: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        return <div className={fadeIn}>
            <PrimaryButton
                text='Continue'
                styles={panelButtonsStyles}
                disabled={optionView === null}
                onClick={() => { setView(optionView?.data!); }}
            />
            <DefaultButton
                text='Cancel'
                styles={panelButtonsStyles}
                onClick={() => { props.onCancel?.(); }}
            />
        </div>;
    }

    const onRenderAdd: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        // creating a new stop loss
        // if there no new stop loss, lets create on first render, set state will cause a second re-render tick
        if (entityCollection?.length !== 1) {
            const t = createAccountsReceivable(props.state)
            setEntityCollection([{ item: t, selected: true }]);
            return <div>Accounts Receivable not available!</div>
        }


        return (
            <div className={fadeIn}>
                <TextField
                    label='Name'
                    value={entityCollection[0].item.name ?? ''}
                    onChange={(event, value) => {
                        setEntityCollection(xCollection => {
                            const nCollection = [...xCollection!];
                            nCollection[0].item.name = value!;
                            return nCollection;
                        });
                    }}
                />
                <Separator />
            </div>
        )
    }

    const onRenderAddFooter: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        return <div className={fadeIn}>
            <PrimaryButton
                text='Continue'
                styles={panelButtonsStyles}
                disabled={!entityCollection || entityCollection.length !== 1 || !entityCollection[0].item.name}
                onClick={() => {
                    const items = entityCollection
                        ?.filter(q => q.selected)
                        .map(q => q.item);

                    if (items)
                        dispatch(addUpdateAccountsReceivable(items));

                    setEntityCollection(null);
                    props.onDismiss?.();
                }}
            />
            <DefaultButton
                text='Cancel'
                styles={panelButtonsStyles}
                onClick={() => { props.onCancel?.(); }}
            />
        </div>;
    }

    const onRenderSelect: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        // TODO this needs to be a side effect rather than part of render cycle
        if (!entityCollection) {
            const collection = props.state.data.json.claimsARs.map(t => {
                return {
                    item: t,
                    selected: false
                } as (IPickItem<FAM_IAccountsReceivable>)
            });

            setEntityCollection(collection);

            return (
                <div>
                    Not Available
                </div>
            );
        }

        return <Stack tokens={{ childrenGap: '15px' }} className={fadeIn}>
            <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                Choose Accounts Receivable
            </Text>
            <Stack
                horizontal
                wrap
                tokens={{ childrenGap: 30 }}
            >
                {entityCollection?.map(eachEntity => {

                    if (eachEntity.item.deactivated)
                        return <></>;

                    return <AccountsReceivableCard
                        key={eachEntity.item.id}
                        accountsReceivable={eachEntity.item}
                        selected={eachEntity.selected}
                        icons={ECardIcon.link}
                        onClick={() => {
                            const newState = updateArrayReplace(entityCollection, eachEntity, { ...eachEntity, selected: !eachEntity.selected });
                            setEntityCollection(newState);
                        }}
                    />

                })}
            </Stack>
        </Stack>;
    }

    const onRenderSelectFooter: IRenderFunction<IPanelProps> = () => {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={!entityCollection || !entityCollection.some(q => q.selected)}
                onClick={() => {
                    props.onConfirm?.({
                        value: {
                            ARSelected: entityCollection?.find(q => q.selected)?.item
                        }
                    });
                }}
            />
            <DefaultButton text='Cancel' onClick={() => { props.onCancel?.(); }} />
        </div>;
    }

    const onRenderCopy = () => {
        return <div className={fadeIn}>
            <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                Choose Fees And Premium
            </Text>
            <Stack
                horizontal
                horizontalAlign="start"
                tokens={sectionStackTokens}
                wrap
                verticalFill
            >
                {
                    loading &&
                    <Spinner label="Please wait..." ariaLive="assertive" labelPosition="right" size={SpinnerSize.large} styles={{ root: { padding: '25px' } }} />
                }

                {entityCollection?.map(eachEntity => {

                    if (eachEntity.item.deactivated)
                        return <></>;

                    return <AccountsReceivableCard
                        key={eachEntity.item.id}
                        accountsReceivable={eachEntity.item}
                        selected={eachEntity.selected}
                        icons={ECardIcon.copy}
                        onClick={() => {
                            const newState = updateArrayReplace(entityCollection, eachEntity, { ...eachEntity, selected: !eachEntity.selected });
                            setEntityCollection(newState);
                        }}
                    />
                })}

            </Stack>
        </div>;
    }

    const onRenderCopyFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton
                text='Pick'
                styles={panelButtonsStyles}
                disabled={!entityCollection?.find(t => t.selected)}
                onClick={() => {

                    const items = entityCollection?.filter(q => q.selected).map(q => q.item) ?? [];

                    items.forEach(e => { e.name = e.name + ' - Copied'; });

                    dispatch(addUpdateAccountsReceivable(items));

                    setEntityCollection(null);
                    setImportSnapshot(null);

                    props.onDismiss?.();

                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    const onRenderImport = () => {

        return <Stack tokens={{ childrenGap: '15' }}>

            <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                Copy Accounts Receivable
            </Text>

            <SnapshotLookup
                onPick={async (value) => {
                    const snapshot = await dataSource.getGroupConfigurationSnapshot(value?.id!);
                    setImportSnapshot(snapshot);
                }}
                searchOnMount={true}
            />

            {
                loading &&
                <Spinner label="Please wait..." ariaLive="assertive" labelPosition="right" size={SpinnerSize.large} styles={{ root: { padding: '25px' } }} />
            }
            <Stack
                horizontal
                horizontalAlign="start"
                tokens={sectionStackTokens}
                wrap
                verticalFill
            >
                {entityCollection?.map(eachEntity => {

                    if (eachEntity.item.deactivated)
                        return <></>;

                    return <AccountsReceivableCard
                        key={eachEntity.item.id}
                        accountsReceivable={eachEntity.item}
                        selected={eachEntity.selected}
                        icons={ECardIcon.copy}
                        onClick={() => {
                            const newState = updateArrayReplace(entityCollection, eachEntity, { ...eachEntity, selected: !eachEntity.selected });
                            setEntityCollection(newState);
                        }}
                    />
                })}
            </Stack>
        </Stack>;
    }

    const onRenderImportFooter = () => {
        return <div className={fadeIn}>
            <PrimaryButton
                text='Pick'
                styles={panelButtonsStyles}
                disabled={!entityCollection || !entityCollection.some(q => q.selected)}
                onClick={() => {

                    const items = entityCollection?.filter(q => q.selected).map(q => q.item) ?? [];

                    items.forEach(e => { e.name = e.name + ' - Copied'; });

                    dispatch(addUpdateAccountsReceivable(items));

                    setEntityCollection(null);
                    setImportSnapshot(null);

                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }


    const onRenderBody: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        let onRender = onRenderEntry;
        switch (view) {
            case EView.entry:
                onRender = onRenderEntry;
                break;

            case EView.add:
                onRender = onRenderAdd;
                break;

            case EView.select:
                onRender = onRenderSelect;
                break;

            case EView.copy:
                onRender = onRenderCopy;
                break;

            case EView.import:
                onRender = onRenderImport;
                break;
        }

        return (
            <div>
                <Separator />
                {onRender()}
            </div>
        );
    }

    const onRenderFooter: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        let onRender = onRenderEntry;

        switch (view) {
            case EView.entry:
                onRender = onRenderEntryFooter;
                break;

            case EView.add:
                onRender = onRenderAddFooter;
                break;

            case EView.select:
                onRender = onRenderSelectFooter;
                break;

            case EView.copy:
                onRender = onRenderCopyFooter;
                break;

            case EView.import:
                onRender = onRenderImportFooter;
                break;
        }

        return onRender();
    }
    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onCancel}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            headerText="Accounts Receivable | Manage"
            onRenderFooterContent={onRenderFooter}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    )
}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(AccountsReceivableWizard);


